import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, ProviderToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import {
  ChallengeActivityHttpModel,
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityService,
} from '@examdojo/challenge';
import { ButtonComponent } from '@examdojo/core/button';
import { DebuggingContextService } from '@examdojo/core/debugging';
import { IconComponent } from '@examdojo/core/icon';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { SmallLayoutComponent, WideLayoutComponent } from '@examdojo/layout';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService, QUESTION_CONTEXT } from '@examdojo/question';
import { TranslocoPipe } from '@jsverse/transloco';
import { catchError, EMPTY, forkJoin, from, map, merge, Observable, of, switchMap, take } from 'rxjs';
import { QuestionComponent } from '../../../question-v2';
import { FooterPanelComponent } from '../../../shared/footer-panel/footer-panel.component';
import { ChallengeActivityQuestionAttemptsRealtimeService } from '../effects/challenge-activity-question-attempts-realtime.service';
import { ChallengeActivityQuestionStatusEffect } from '../effects/challenge-activity-question-status.effect';
import { FetchAssignmentQuestionsOnActiveChallengeActivityService } from '../effects/fetch-assignment-questions-on-active-challenge-activity.service';
import { FetchChallengeActivityQuestionAttemptsOnActiveService } from '../effects/fetch-challenge-activity-question-attempts-on-active.service';
import { FetchMcqResponseOnActiveChallengeActivityService } from '../effects/fetch-mcq-response-on-active-challenge-activity.service';
import { provideMultiAttemptQuestionSubmitReady } from '../effects/multi-attempt-question-submit-ready.service';
import {
  MultiAttemptSelfGradingSelectionService,
  provideMultiAttemptSelfGradingSelection,
} from '../effects/multi-attempt-self-grading-selection.service';
import { QuestionAttemptRealtimeChallengeService } from '../effects/question-attempt-realtime-challenge.service';
import { provideSyncedMcqSelection, SyncedMcqSelectionService } from '../effects/synced-mcq-selection.service';
import { ExamTimeComponent, ExamTimeService } from '../exam-time';
import { ChallengeGradingLoadingComponent } from '../grading-loading/challenge-grading-loading.component';
import { ChallengeActivityQuestionSelectorComponent } from '../question-selector/challenge-activity-question-selector.component';
import { ChallengeActivityQuestionSelectorService } from '../question-selector/challenge-activity-question-selector.service';
import { SubmitConfirmationDialogService } from '../submit-confirmation/submit-confirmation-dialog.service';
import { ChallengeDialogService } from './challenge-dialog.service';

@Component({
  selector: 'dojo-challenge-dialog-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './challenge-dialog-container.component.html',
  imports: [
    FooterPanelComponent,
    ButtonComponent,
    TranslocoPipe,
    ChallengeActivityQuestionSelectorComponent,
    QuestionComponent,
    SmallLayoutComponent,
    WideLayoutComponent,
    NgClass,
    ExamTimeComponent,
    IconComponent,
    ChallengeGradingLoadingComponent,
  ],
  animations: [
    getAnimationAppearDisappear({
      leave: AnimationDuration.None,
    }),
  ],
  host: { class: 'flex flex-col h-full' },
  providers: [
    ChallengeActivityQuestionSelectorService,
    SubmitConfirmationDialogService,
    ExamTimeService,
    FetchAssignmentQuestionsOnActiveChallengeActivityService,
    FetchChallengeActivityQuestionAttemptsOnActiveService,
    FetchMcqResponseOnActiveChallengeActivityService,
    QuestionAttemptRealtimeChallengeService,
    ChallengeActivityQuestionAttemptsRealtimeService,
    provideSyncedMcqSelection(),
    provideMultiAttemptSelfGradingSelection(),
    provideMultiAttemptQuestionSubmitReady(),
    ChallengeActivityQuestionStatusEffect,
    {
      provide: QUESTION_CONTEXT,
      useValue: { showHints: true, optionsSelectable: true, revealSolutionOnWrongAnswer: true },
    },
  ],
})
export class ChallengeDialogContainerComponent {
  constructor(
    private readonly challengeDialogService: ChallengeDialogService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly destroyRef: DestroyRef,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly challengeActivityQuestionSelectorService: ChallengeActivityQuestionSelectorService,
    private readonly submitConfirmationDialogService: SubmitConfirmationDialogService,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly examTimeService: ExamTimeService,
    private readonly multiAttemptSelfGradingSelectionService: MultiAttemptSelfGradingSelectionService,
  ) {
    merge(this.debuggingContextService.syncDebuggingContext('Activity id', this.inProgressChallengeActivityId$))
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [
    FetchAssignmentQuestionsOnActiveChallengeActivityService,
    FetchChallengeActivityQuestionAttemptsOnActiveService,
    FetchMcqResponseOnActiveChallengeActivityService,
    QuestionAttemptRealtimeChallengeService,
    ChallengeActivityQuestionAttemptsRealtimeService,
    SyncedMcqSelectionService,
    MultiAttemptSelfGradingSelectionService,
    ChallengeActivityQuestionStatusEffect,
  ].map((service: ProviderToken<unknown>) => inject(service));

  readonly inProgressChallengeActivityId$: Observable<string | undefined> =
    this.challengeActivityQuery.inProgressChallengeActivityId$;

  readonly isTransitioningBetweenQuestions =
    this.challengeActivityQuestionSelectorService.isTransitioningBetweenQuestions;

  private readonly isTimeUp$ = this.examTimeService.isTimeUp$;

  readonly state = connectState({
    inProgressActivityId: this.inProgressChallengeActivityId$,
    challengeActivityStatus: this.challengeActivityQuery.ongoingActivityStatus$,
    allQuestionAttemptsDone: this.challengeActivityQuestionSelectorService.allQuestionAttemptsDone$,
    activeQuestionAttempt: this.questionAttemptQuery.active$,
    isQuestionMultipleChoice: this.questionAttemptQuery.isQuestionMultipleChoice$,
    shouldShowSubmitButton: this.challengeActivityQuery.ongoingActivityStatus$.pipe(
      map((status) => status === 'IN_PROGRESS'),
    ),
    isTimeUp: this.isTimeUp$,
    shouldShowRightSidebar: this.challengeActivityQuery.ongoingActivity$.pipe(map((activity) => !!activity)),
  });

  onQuestionCompleted(action: 'complete' | 'stop') {
    if (action === 'stop') {
      this.completeChallengeActivity('DISCARDED').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    if (action === 'complete') {
      this.completeChallengeActivity('COMPLETED').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  gradeChallengeActivity() {
    this.examTimeService.isTimeUp$
      .pipe(
        take(1),
        switchMap((isTimeUp) => {
          return this.gradeChallengeAfterConfirmation(!isTimeUp);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  submitSelfGradingAndComplete() {
    const submissions$ = this.multiAttemptSelfGradingSelectionService
      .generateAllGradingPayloads()
      .map(({ attemptId, payload }) =>
        this.questionAttemptEntityService
          .submitForSelfGrading(payload, attemptId, { setStatus: false })
          .pipe(catchError(() => of(null))),
      );

    if (submissions$.length === 0) {
      return this.completeChallengeActivity('COMPLETED').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    // TODO: refactor when bulk self grade endpoint will be possible
    return forkJoin(submissions$)
      .pipe(
        switchMap(() => this.completeChallengeActivity('COMPLETED')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private gradeChallengeAfterConfirmation(showConfirmation: boolean) {
    const activityId = this.state.inProgressActivityId;
    assertNonNullable(activityId, 'activityId');

    const confirmation$ = showConfirmation ? from(this.submitConfirmationDialogService.openDialog()) : of(true);
    return confirmation$.pipe(
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return this.challengeActivityService.gradeChallengeActivity(activityId).pipe(
          switchMap(() => {
            return this.challengeActivityQuestionAttemptService.markAllQuestionAttemptsAsDone(activityId);
          }),
        );
      }),
    );
  }

  private completeChallengeActivity(status: ChallengeActivityHttpModel['status']) {
    const activityId = this.state.inProgressActivityId;
    assertNonNullable(activityId, 'activityId');

    return this.challengeActivityService.completeChallengeActivityWithStatus(activityId, status);
  }
}
